import { CheckDone01, ChevronRight } from '@untitledui/icons';
import {
  type FocusEvent as ReactFocusEvent,
  useCallback,
  useState,
} from 'react';

import { useCurrentUserEntity } from '@/hooks/useCurrentUserEntity';
import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/lib/base/buttons/button';
import { isInsideFloatingOverlay } from '@/lib/base/overlays/floating-overlay';
import { Tooltip } from '@/lib/base/tooltip/tooltip';
import {
  AssociatedPipelineStageInput,
  type StageAssociationValue,
} from '@/lib/crm/components/properties/inputs/AssociatedPipelineStageInput';
import { PipelineStageValueBadge } from '@/lib/crm/components/properties/labels/PipelineStageValueBadge';
import { useNavigateToEntity } from '@/lib/crm/hooks/useNavigateToEntity';
import { usePipelines } from '@/lib/crm/hooks/usePipelines';
import { useCompletedTaskStage } from '@/lib/crm/record/task/useCompletedTaskStage';
import { logger } from '@/lib/logger';
import {
  resolveTaskTitle,
  TASK_STAGE_ASSOCIATION_TYPE_NAME,
} from '@/pages/calendar/utils/taskUtils';
import type { CrmObject } from '@/types/crm';

import { useCompleteTask } from '../hooks/useCompleteTask';

interface TaskRowProps {
  task: CrmObject;
}

export function TaskRow({ task }: TaskRowProps) {
  const navigateToEntity = useNavigateToEntity();
  const { orgId: orgIdStr, userId } = useCurrentUserEntity();
  const orgId = parseInt(orgIdStr ?? '0', 10);
  const { hasPermission } = usePermissions();
  const { completeTask, moveTaskToStage, isCompleting } =
    useCompleteTask(orgId);
  const { pipelines: taskPipelines } = usePipelines(orgId, 'ahoy_task');

  const [isEditingStage, setIsEditingStage] = useState(false);

  const canComplete = hasPermission('ahoy_task', 'ahoy_association_added');

  const currentStageEntityId =
    task.associations?.find(
      a => a.association_type_name === TASK_STAGE_ASSOCIATION_TYPE_NAME
    )?.target_entity_id ?? null;

  const { completedStageEntityId } = useCompletedTaskStage(
    orgId,
    currentStageEntityId,
    canComplete
  );

  const title = resolveTaskTitle(task);

  const handleClick = useCallback(() => {
    navigateToEntity(task.entity_id, 'ahoy_task');
  }, [navigateToEntity, task.entity_id]);

  const handleComplete = useCallback(async () => {
    if (!completedStageEntityId || !userId) {
      return;
    }
    try {
      await completeTask(task, completedStageEntityId, String(userId));
    } catch (err: unknown) {
      // useCompleteTask already shows a toast and rolls back the optimistic update.
      // Log here as a UI-layer fallback in case the hook's log is not captured.
      logger.error(
        'TaskRow: task completion rejected',
        err instanceof Error ? err : new Error(String(err)),
        { task_entity_id: task.entity_id }
      );
    }
  }, [completeTask, task, completedStageEntityId, userId]);

  const handleStartEditStage = useCallback(() => {
    setIsEditingStage(true);
  }, []);

  const handleStageEditClose = useCallback(() => {
    setIsEditingStage(false);
  }, []);

  const handleStageInputBlur = useCallback(
    (e: ReactFocusEvent<HTMLDivElement>) => {
      const related = e.relatedTarget;
      const isInsideContainer =
        related instanceof Node && e.currentTarget.contains(related);
      if (
        !isInsideContainer &&
        !isInsideFloatingOverlay(related, { surface: e.currentTarget })
      ) {
        handleStageEditClose();
      }
    },
    [handleStageEditClose]
  );

  const handleStageChange = useCallback(
    async (assoc: StageAssociationValue | null) => {
      setIsEditingStage(false);
      if (
        assoc == null ||
        !userId ||
        assoc.target_entity_id === currentStageEntityId
      ) {
        return;
      }
      const targetIsTerminal = taskPipelines.some(p =>
        p.stages.some(
          s => s.entity_id === assoc.target_entity_id && s.is_terminal === true
        )
      );
      try {
        await moveTaskToStage(task, assoc.target_entity_id, String(userId), {
          removeFromList: targetIsTerminal,
        });
      } catch (err: unknown) {
        logger.error(
          'TaskRow: task stage change rejected',
          err instanceof Error ? err : new Error(String(err)),
          { task_entity_id: task.entity_id }
        );
      }
    },
    [userId, currentStageEntityId, taskPipelines, moveTaskToStage, task]
  );

  const isCompletingThisTask = isCompleting(task.entity_id);

  let stageContent = (
    <PipelineStageValueBadge
      value={currentStageEntityId}
      orgId={orgId}
      size='sm'
      interactive={false}
    />
  );

  if (canComplete) {
    if (isEditingStage) {
      stageContent = (
        <div className='w-44' onBlur={handleStageInputBlur}>
          <AssociatedPipelineStageInput
            entityType='ahoy_task'
            associationTypeName={TASK_STAGE_ASSOCIATION_TYPE_NAME}
            value={currentStageEntityId}
            size='xs'
            disableAutoSelect
            defaultOpen
            onChange={handleStageChange}
          />
        </div>
      );
    } else {
      stageContent = (
        <Tooltip title='Change stage' placement='top'>
          <button
            type='button'
            onClick={handleStartEditStage}
            aria-label='Change stage'
            className='flex items-center'
          >
            <PipelineStageValueBadge
              value={currentStageEntityId}
              orgId={orgId}
              size='sm'
              interactive={false}
            />
          </button>
        </Tooltip>
      );
    }
  }

  return (
    <div className='w-full flex items-center h-14 bg-primary border border-secondary rounded-[4px] hover:bg-primary_hover transition-colors'>
      <button
        type='button'
        onClick={handleClick}
        className='flex-1 flex items-center gap-3 h-full px-3.5 text-left min-w-0'
      >
        <CheckDone01 className='size-4 text-neutral-400 shrink-0' />
        <span className='flex-1 text-[13px] font-medium text-[var(--color-text-primary)] truncate'>
          {title}
        </span>
        <ChevronRight className='size-4 text-neutral-400 shrink-0' />
      </button>
      <div className='flex items-center gap-2 pr-2 shrink-0'>
        {stageContent}
        {canComplete && (
          <Tooltip title='Complete' placement='top'>
            <Button
              color='tertiary'
              size='sm'
              iconLeading={CheckDone01}
              aria-label='Complete task'
              onClick={handleComplete}
              isDisabled={
                isCompletingThisTask || completedStageEntityId == null
              }
              className='shrink-0'
            />
          </Tooltip>
        )}
      </div>
    </div>
  );
}
